import { Component, ChangeDetectionStrategy, inject, signal } from "@angular/core";
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { SelectModule } from "primeng/select";
import { TextareaModule } from "primeng/textarea";
import { FloatLabelModule } from "primeng/floatlabel";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";

import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { selectQuoteId } from "src/app/store/pages/quote/quote.selectors";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";

interface LostReason {
    id: number;
    name: string;
    value: string;
}

@Component({
    selector: "app-mark-as-lost-reason-dialog",
    templateUrl: "./mark-as-lost-reason-dialog.component.html",
    styleUrls: ["./mark-as-lost-reason-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        CoreErrorsComponent,
        ButtonModule,
        SelectModule,
        TextareaModule,
        FloatLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
    ],
})
export class MarkAsLostReasonDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    public readonly ref = inject(DynamicDialogRef);
    private readonly store = inject(Store);

    // =========================================================================
    // CONSTANTS
    // =========================================================================
    public readonly lostReasons: LostReason[] = [
        { id: 1, name: "MARK_AS_LOST.REASON_1", value: "Forlorad_till_konkurrent" },
        { id: 2, name: "MARK_AS_LOST.REASON_2", value: "Ingen Budget" },
        { id: 3, name: "MARK_AS_LOST.REASON_3", value: "Inget Beslut / Återkoppling" },
        { id: 4, name: "MARK_AS_LOST.REASON_4", value: "Pris" },
        { id: 5, name: "MARK_AS_LOST.REASON_5", value: "Övrigt" },
    ];

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly quoteId = toSignal(this.store.select(selectQuoteId));

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public readonly showCommentField = signal(false);

    // =========================================================================
    // FORM STATE
    // =========================================================================
    public readonly form = new FormGroup({
        lostReason: new FormControl<string | null>(null, [Validators.required]),
        lostComment: new FormControl(""),
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public onLostReasonChange(event: { value: string }): void {
        this.showCommentField.set(event.value === "Övrigt");
        const control = this.form.get("lostComment");
        if (this.showCommentField()) {
            control?.setValidators([Validators.required]);
        } else {
            control?.clearValidators();
        }
        control?.updateValueAndValidity();
    }

    public submit(): void {
        const quoteId = this.quoteId();
        if (quoteId) {
            const request = {
                lostReason: this.form.get("lostReason")?.value,
                lostComment: this.form.get("lostComment")?.value,
            };
            this.store.dispatch(
                QuoteActions.updateMarkAsLost({
                    quoteId,
                    request,
                    dialogKey: 'MARK_QUOTE_AS_LOST',
                }),
            );
        }
        this.ref.close(true);
    }

    public closeDialog(): void {
        this.ref.close(false);
    }
}
